import React, { KeyboardEvent, useContext } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { VolumeHigh, VolumeOff } from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { VolumeChangedContext } from "../../contexts/VolumeChangedContext";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";

import { ControlBarButton } from "./ControlBarButton";

const VolumeControls = styled.div`
  display: flex;
`;

const VolumeButton = styled(ControlBarButton)`
  &:hover + div,
  &:focus + div {
    visibility: visible;
  }
`;

const VolumeSlider = styled.div<{
  uiSizeInPx: number;
  visible: boolean;
}>`
  height: 32px;
  margin-left: 8px;
  position: relative;
  transition: visibility 1s;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
  width: ${({ uiSizeInPx }) => (uiSizeInPx < 500 ? "50px" : "75px")};

  &:hover,
  &:focus-within {
    visibility: visible;
  }

  div {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    position: absolute;
    width: 100%;
  }

  progress {
    appearance: none;
    background-color: rgb(255 255 255 / 17%);
    border-radius: 2px;
    height: 4px;
    width: 100%;

    &::-webkit-progress-bar {
      background-color: rgb(255 255 255 / 17%);
      border-radius: 2px;
    }

    &::-webkit-progress-value {
      background-color: ${darkTheme.BACKGROUND_NEGATIVE};
      border-radius: 2px;
    }

    &::-moz-progress-bar {
      background-color: ${darkTheme.BACKGROUND_NEGATIVE};
    }
  }

  input {
    appearance: none;
    background: transparent;
    cursor: pointer;
    height: 100%;
    margin: 0;
    position: absolute;
    width: 100%;

    &::-webkit-slider-thumb {
      appearance: none;
      background: ${darkTheme.BACKGROUND_NEGATIVE};
      border-radius: 50%;
      cursor: pointer;
      height: 16px;
      width: 16px;
    }

    &::-moz-range-thumb {
      background: ${darkTheme.BACKGROUND_NEGATIVE};
      border-radius: 50%;
      border: none;
      cursor: pointer;
      height: 16px;
      width: 16px;
    }
  }
`;

export const Volume = () => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isMuted = useStoreState.isMuted();
  const player = useStoreState.player();
  const uiSizeInPx = useStoreState.uiSizeInPx();
  const volume = useStoreState.volume();

  const { language } = useEmbedParametersContext();

  const volumeLevel = isMuted ? 0 : volume;
  const label = i18n(isMuted ? "poista_mykistys" : "mykista", { language });
  const ariaLabel = i18n(
    isMuted ? "poista_mykistys_aria_label" : "mykista_aria_label",
    { language }
  );
  const volumeSliderVisible = useContext(VolumeChangedContext);

  const onKeyEvent = (e: KeyboardEvent) => {
    if (e.key.startsWith("Arrow")) {
      e.stopPropagation();
    }
  };

  return (
    <VolumeControls>
      <VolumeButton
        aria-keyshortcuts="m"
        aria-label={ariaLabel}
        onClick={() => {
          player.setMuted(!isMuted, true);
        }}
        tooltip={label}>
        {isMuted ? <VolumeOff /> : <VolumeHigh />}
      </VolumeButton>
      {!device.isMobile && (
        <VolumeSlider uiSizeInPx={uiSizeInPx} visible={volumeSliderVisible}>
          <div>
            <progress aria-hidden={true} value={volumeLevel} max={1} />
          </div>
          <input
            aria-label={i18n("volume", { language })}
            max={1}
            min={0}
            onChange={event => {
              player.setVolume(parseFloat(event.target.value), true);
            }}
            onKeyDown={e => onKeyEvent(e)}
            onKeyUp={e => onKeyEvent(e)}
            step={0.02}
            type="range"
            value={volumeLevel}
          />
        </VolumeSlider>
      )}
    </VolumeControls>
  );
};
